import * as notificationsApi from '@services/notifications';

export type NotificationType = 
	| 'offer_received'           // Seeker: when they get a new offer
	| 'offer_accepted'           // Gardener/Company: when their offer is accepted
	| 'offer_rejected'           // Gardener/Company: when their offer is rejected
	| 'new_message'              // Seeker/Gardener: new message in conversation
	| 'deadline_near'            // Everyone: when project deadline is approaching
	| 'deadline_overdue'         // Everyone: when project deadline has passed
	| 'plan_published'           // Gardener: when a new plan is published
	| 'project_status_changed'   // Everyone: when project status changes
	| 'task_assigned'            // Gardener/Company: when a task is assigned
	| 'invite_received'          // Recipient: received a company invite
	| 'invite_responded'         // Company: invite accepted or declined
	| 'direct_plan_created'      // Company: seeker created direct plan from invite
	| 'task_issue_reported'      // Company: issue reported on task
	| 'project_replanned';       // Gardener: project timeline has changed

export type Notification = {
	id: string;
	userId: string;              // Who should see this notification
	type: NotificationType;
	title: string;
	message: string;
	read: boolean;
	createdAt: string;
	// Optional metadata for navigation
	relatedId?: string;          // ID of related entity (plan, offer, project, conversation, etc.)
	relatedType?: 'plan' | 'offer' | 'project' | 'task' | 'conversation';
	url?: string;                // Direct URL to navigate to (path-only, e.g. /app/conversation?conversation_id=1)
};

// Cache for notifications to avoid repeated API calls
let notificationsCache: Notification[] = [];
let cacheUserId: string | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 30000; // 30 seconds

/**
 * Get notifications for a user from the API
 */
export async function getNotificationsForUser(userId: string, userRole?: string): Promise<Notification[]> {
	try {
		// Check cache first
		const now = Date.now();
		if (cacheUserId === userId && (now - cacheTimestamp) < CACHE_DURATION) {
			return notificationsCache;
		}

		// Fetch from API
		const apiNotifications = await notificationsApi.getNotifications(userId, false);
		
		// Filter by user role if needed (for backward compatibility)
		let filtered = apiNotifications;
		if (userRole === 'seeker') {
			// For demo purposes: show offer_received notifications to all seekers
			// In production, this would use the actual plan.seekerUserId
			filtered = apiNotifications.filter(n => 
				n.userId === userId || n.type === 'offer_received'
			);
		} else {
			filtered = apiNotifications.filter(n => n.userId === userId);
		}

		// Update cache
		notificationsCache = filtered;
		cacheUserId = userId;
		cacheTimestamp = now;

		// Dispatch event for components listening to updates
		window.dispatchEvent(new CustomEvent('notificationsUpdated'));

		return filtered;
	} catch (error) {
		console.error('Failed to fetch notifications:', error);
		// Return cached data if available, otherwise empty array
		return cacheUserId === userId ? notificationsCache : [];
	}
}

/**
 * Get unread count for a user
 */
export async function getUnreadCountForUser(userId: string, userRole?: string): Promise<number> {
	try {
		const count = await notificationsApi.getUnreadCount(userId);
		return count;
	} catch (error) {
		console.error('Failed to fetch unread count:', error);
		return 0;
	}
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
	try {
		await notificationsApi.markAsRead(notificationId);
		// Update cache
		const notification = notificationsCache.find(n => n.id === notificationId);
		if (notification) {
			notification.read = true;
		}
		window.dispatchEvent(new CustomEvent('notificationsUpdated'));
	} catch (error) {
		console.error('Failed to mark notification as read:', error);
		throw error;
	}
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllAsRead(userId: string): Promise<void> {
	try {
		await notificationsApi.markAllAsRead(userId);
		// Update cache
		notificationsCache = notificationsCache.map(n => 
			n.userId === userId && !n.read ? { ...n, read: true } : n
		);
		window.dispatchEvent(new CustomEvent('notificationsUpdated'));
	} catch (error) {
		console.error('Failed to mark all notifications as read:', error);
		throw error;
	}
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
	try {
		await notificationsApi.deleteNotification(notificationId);
		
		// Update cache
		notificationsCache = notificationsCache.filter(n => n.id !== notificationId);
		window.dispatchEvent(new CustomEvent('notificationsUpdated'));
	} catch (error) {
		console.error('Failed to delete notification:', error);
		throw error;
	}
}

/**
 * Create a new notification
 */
export async function createNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'read'>): Promise<Notification> {
	try {
		const payload: notificationsApi.CreateNotificationRequest = {
			userId: notification.userId,
			type: notification.type,
			title: notification.title,
			message: notification.message,
			relatedId: notification.relatedId,
			relatedType: notification.relatedType,
			url: notification.url,
		};
		const created = await notificationsApi.createNotification(payload);

		// Invalidate cache if it's for the same user
		if (cacheUserId === notification.userId) {
			cacheTimestamp = 0; // Force refresh
		}

		window.dispatchEvent(new CustomEvent('notificationsUpdated'));
		// Notify UI to show a popup for the new notification (listeners can filter by userId)
		window.dispatchEvent(new CustomEvent('newNotification', { detail: created }));

		return created;
	} catch (error) {
		console.error('Failed to create notification:', error);
		throw error;
	}
}

// Helper functions to create specific notification types
export async function notifyOfferReceived(seekerUserId: string, offerId: string, planId: string, gardenerName: string, planTitle?: string) {
	const title = planTitle || 'your plan';
	
	await createNotification({
		userId: seekerUserId,
		type: 'offer_received',
		title: 'New Offer Received',
		message: `${gardenerName} sent an offer for "${title}"`,
		relatedId: offerId,
		relatedType: 'offer',
		url: `/app/seeker/plan/${planId}/offers?offer_id=${encodeURIComponent(offerId)}`
	});
}

export async function notifyOfferAccepted(gardenerId: string, offerId: string, planId: string, planTitle: string, projectId?: string) {
	await createNotification({
		userId: gardenerId,
		type: 'offer_accepted',
		title: 'Offer Accepted! 🎉',
		message: `Your offer for "${planTitle}" has been accepted`,
		relatedId: offerId,
		relatedType: 'offer',
		url: projectId ? `/app/gardener/project/${projectId}` : `/app/projects`
	});
}

export async function notifyOfferRejected(gardenerId: string, offerId: string, planId: string, planTitle: string) {
	await createNotification({
		userId: gardenerId,
		type: 'offer_rejected',
		title: 'Offer Not Selected',
		message: `Your offer for "${planTitle}" was not selected`,
		relatedId: offerId,
		relatedType: 'offer',
		url: `/app/plans`
	});
}

export async function notifyDeadlineNear(userId: string, projectId: string, projectName: string, daysUntil: number) {
	const urgency = daysUntil <= 1 ? 'overdue' : daysUntil <= 3 ? 'urgent' : 'near';
	const title = urgency === 'overdue' 
		? '⚠️ Deadline Overdue' 
		: urgency === 'urgent' 
		? '⚠️ Deadline Approaching' 
		: 'Deadline Reminder';
	
	const message = urgency === 'overdue'
		? `"${projectName}" deadline has passed`
		: `"${projectName}" deadline is in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`;

	await createNotification({
		userId,
		type: urgency === 'overdue' ? 'deadline_overdue' : 'deadline_near',
		title,
		message,
		relatedId: projectId,
		relatedType: 'project',
		url: `/app/projects`
	});
}

export async function notifyPlanPublished(gardenerId: string, planId: string, planTitle: string, city: string) {
	await createNotification({
		userId: gardenerId,
		type: 'plan_published',
		title: 'New Plan Available',
		message: `"${planTitle}" in ${city} is now available`,
		relatedId: planId,
		relatedType: 'plan',
		url: `/app/seeker/plan/${planId}`
	});
}

export async function notifyProjectStatusChanged(userId: string, projectId: string, projectName: string, oldStatus: string, newStatus: string) {
	await createNotification({
		userId,
		type: 'project_status_changed',
		title: 'Project Status Updated',
		message: `"${projectName}" status changed from ${oldStatus} to ${newStatus}`,
		relatedId: projectId,
		relatedType: 'project',
		url: `/app/projects`
	});
}

export async function notifyTaskAssigned(userId: string, taskId: string, taskTitle: string) {
	await createNotification({
		userId,
		type: 'task_assigned',
		title: 'New Task Assigned',
		message: `You've been assigned: "${taskTitle}"`,
		relatedId: taskId,
		relatedType: 'task',
		url: `/app/tasks`
	});
}

// Check for upcoming deadlines (should be called periodically)
// This function should be called from a component that has access to projects and users
export async function checkDeadlines(projects: Array<{ id: string; name: string; dueDate?: string; owner: string }>, userIdMap?: Map<string, string>) {
	const now = new Date();
	
	// Get current notifications to check for duplicates
	let currentNotifications: Notification[] = [];
	try {
		// We need to check notifications for all users, but this is a bit complex
		// For now, we'll check per user as we iterate
	} catch (error) {
		console.error('Failed to check existing notifications:', error);
	}
	
	for (const project of projects) {
		if (!project.dueDate) continue;
		
		const dueDate = new Date(project.dueDate);
		const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
		
		// Notify if deadline is within 3 days or overdue (but not more than 7 days past)
		if (daysUntil <= 3 && daysUntil >= -7) {
			// Get userId from owner name (in real app, owner would be userId)
			// project.owner from API is a display name (gardener/client), not a user id - we need a numeric id
			const userId = userIdMap?.get(project.owner) || project.owner;
			// Only create notifications when we have a valid numeric user id (FK in DB references users.id)
			if (!/^\d+$/.test(String(userId))) continue;

			try {
				// Check if we already notified for this deadline window recently (within last 24 hours)
				const userNotifications = await getNotificationsForUser(userId);
				const existingNotification = userNotifications.find(
					n => n.relatedId === project.id && 
					n.userId === userId &&
					n.type === (daysUntil < 0 ? 'deadline_overdue' : 'deadline_near') &&
					// Only create new notification if the last one is less than 24 hours old (meaning we already notified recently)
					new Date(n.createdAt).getTime() > now.getTime() - 24 * 60 * 60 * 1000
				);
				
				// Only create notification if we haven't notified in the last 24 hours
				if (!existingNotification) {
					await notifyDeadlineNear(userId, project.id, project.name, daysUntil);
				}
			} catch (error) {
				console.error(`Failed to check/create deadline notification for project ${project.id}:`, error);
			}
		}
	}
}

